import React from "react";
import SimpleBar from "simplebar-react";
import wiserepLogo from "../assets/images/wiserep-logo.png";
import { Link, useLocation } from "react-router-dom";
import { useAppContext } from '../context/AppContext';

/**
 * Sidebar
 *
 * Two things were wrong here before this rewrite, both worth knowing about:
 *
 * 1. There was no active state at all. The menu was built from plain <Link>
 *    elements with no active logic, while all the `.menuitem-active` CSS lived
 *    in SidebarContent.js — a component that was imported but never rendered.
 *    So the "you are here" indicator, the single most important brand moment in
 *    a dashboard, simply never appeared.
 *
 * 2. The entire nav list was written out twice — once disabled for expired-trial
 *    users, once live for everyone else. Any change to a label, icon or route
 *    had to be made in both copies or they would silently drift apart.
 *
 * Both are fixed by driving the menu from the NAV_ITEMS array below and deriving
 * the disabled state, rather than duplicating the markup.
 */

/**
 * `match` lists additional routes that should keep an item highlighted. A user
 * editing an agent at /update-agent/42 is still "in" Agent Management, so the
 * nav should say so — plain path equality would drop the highlight the moment
 * they open a detail page.
 *
 * `id` values are load-bearing: TourManager.js targets #sidebar-prompt,
 * #sidebar-agent, #sidebar-calls and #sidebar-plans to anchor the product tour.
 * Renaming them silently breaks onboarding.
 */
const NAV_ITEMS = [
    {
        id: "sidebar-getting-started",
        to: "/get-started",
        icon: "ri-route-line",
        label: "Getting Started",
        // Only shown to trial users who haven't finished onboarding.
        visible: ({ isTrial, isCompleted }) => isTrial && !isCompleted,
    },
    { id: "sidebar-dashboard", to: "/dashboard", icon: "ri-home-4-line", label: "Dashboard" },
    { id: "sidebar-prompt", to: "/prompts", icon: "ri-text-block", label: "Prompts", match: ["/create-new-prompt"] },
    { id: "sidebar-agent", to: "/agents", icon: "ri-user-line", label: "Agents", match: ["/create-new-agent", "/update-agent"] },
    {
        id: "sidebar-campaigns",
        to: "/campaigns",
        icon: "ri-megaphone-line",
        label: "Campaigns",
        match: ["/create-new-campaign", "/update-campaign", "/duplicate-campaign"],
    },
    { id: "sidebar-calls", to: "/call-list", icon: "ri-phone-line", label: "Calls", match: ["/make-call"] },
    // Previously also id="sidebar-calls" — a duplicate DOM id, which is invalid
    // and made the tour's querySelector ambiguous.
    { id: "sidebar-chats", to: "/chat-list", icon: "ri-chat-3-line", label: "Chats" },
    { id: "sidebar-test-calls", to: "/test-call-logs", icon: "ri-flask-line", label: "Test Calls" },
    {
        id: "sidebar-plans",
        to: "/pricing-plans",
        icon: "ri-settings-4-line",
        label: "Pricing",
        visible: ({ isTrial, isActiveSubscription }) => isTrial || !isActiveSubscription,
        // The one item that stays reachable once a trial expires — it is the
        // route out of the expired state, so locking it would trap the user.
        availableWhenExpired: true,
    },
    { id: "sidebar-billing", to: "/billing", icon: "ri-money-dollar-box-line", label: "Billing" },
];

/** Collapsible Configuration group. `end` marks routes that must match exactly. */
const CONFIG_ITEMS = [
    { to: "/configuration", label: "General", end: true },
    { to: "/configuration/webhook", label: "Initiate Call Webhook" },
    { to: "/configuration/post-webhook", label: "Post Call Webhook" },
];

function Sidebar(props) {
    const { isTrial, daysRemaining, isCompleted, isActiveSubscription } = useAppContext();
    const { pathname } = useLocation();

    // Once the trial is up, everything except Pricing Plans is locked. The route
    // guard in ProtectedLayout already redirects; this just stops the nav from
    // advertising destinations the user cannot reach.
    const trialExpired = isTrial && daysRemaining < 1;
    const ctx = { isTrial, isCompleted, isActiveSubscription };

    /**
     * Active when the path matches exactly, or when it is a child route of this
     * item (so /configuration/webhook keeps Configuration open), or when it is
     * one of the explicitly declared `match` routes.
     */
    const isActive = (item) => {
        if (pathname === item.to) return true;
        if (item.match?.some((m) => pathname.startsWith(m))) return true;
        return !item.end && item.to !== "/" && pathname.startsWith(`${item.to}/`);
    };

    const configOpen = pathname.startsWith("/configuration");

    const renderItem = (item) => {
        if (item.visible && !item.visible(ctx)) return null;

        const locked = trialExpired && !item.availableWhenExpired;

        return (
            <li key={item.id} id={item.id}>
                {locked ? (
                    // Rendered as a span, not a <Link>: a disabled anchor is still
                    // focusable and still announced as a link to screen readers.
                    <span className="text-muted no-drop" aria-disabled="true">
                        <i className={item.icon}></i>
                        <span> {item.label} </span>
                    </span>
                ) : (
                    <Link to={item.to} className={isActive(item) ? "active" : undefined}>
                        <i className={item.icon}></i>
                        <span> {item.label} </span>
                    </Link>
                )}
            </li>
        );
    };

    // The logo links somewhere the user can actually go — /pricing-plans is the
    // only reachable route once the trial has expired.
    const logoTarget = trialExpired ? "/pricing-plans" : "/dashboard";

    return (
        <div className="vertical-menu overflow-hidden">
            <div data-simplebar className="h-100">
                {props.sidebarExpand ? (
                    <SimpleBar style={{ maxHeight: "100%" }}>
                        <div className="app-sidebar-menu">
                            <div id="sidebar-menu">

                                <div className="logo-box">
                                    <Link to={logoTarget} className="logo logo-dark">
                                        <span className="logo-sm">
                                            <img src={wiserepLogo} alt="Wiserep" height="22" />
                                        </span>
                                        <span className="logo-lg">
                                            <img src={wiserepLogo} alt="Wiserep" height="30" />
                                        </span>
                                    </Link>
                                </div>

                                <ul id="side-menu">
                                    <li className="menu-title">Menu</li>

                                    {NAV_ITEMS.map(renderItem)}

                                    {/* Configuration is a Bootstrap collapse group rather than a
                                        route of its own, so it needs its own open/active handling. */}
                                    {trialExpired ? (
                                        <li>
                                            <span className="text-muted no-drop" aria-disabled="true">
                                                <i className="ri-settings-2-line"></i>
                                                <span> Configuration </span>
                                            </span>
                                        </li>
                                    ) : (
                                        <li className={configOpen ? "menuitem-active" : undefined}>
                                            <a
                                                href="#sidebarDashboards"
                                                data-bs-toggle="collapse"
                                                aria-expanded={configOpen}
                                                className={configOpen ? "active" : undefined}
                                            >
                                                <i className="ri-settings-2-line"></i>
                                                <span> Configuration </span>
                                                <span className="menu-arrow"></span>
                                            </a>
                                            {/* `show` keeps the group expanded on a deep link or
                                                page refresh into one of its children. */}
                                            <div className={`collapse ${configOpen ? "show" : ""}`} id="sidebarDashboards">
                                                <ul className="nav-second-level">
                                                    {CONFIG_ITEMS.map((sub) => (
                                                        <li key={sub.to}>
                                                            <Link
                                                                to={sub.to}
                                                                className={`tp-link ${isActive(sub) ? "active" : ""}`}
                                                            >
                                                                {sub.label}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </li>
                                    )}
                                </ul>

                            </div>

                            <div className="clearfix"></div>
                        </div>
                    </SimpleBar>
                ) : null}
            </div>
        </div>
    );
}

export default Sidebar;
